import type { Doc, CreateLocalScheme, UpdateLocalScheme, DeleteLocalScheme, FetchLocalScheme } from './types';
import { filterConvertation } from './filterConvertation';

// Функция, которая заменяет _kuzzle_info на sysinfo
function renameSysInfo(items: Doc[]) {
	if (!items) {
		return [];
	}
	return items?.map((item) => {
		// Деструктурируем объект, выделяя нужные поля
		const { dbClass, _kuzzle_info, getRef, getBackRef, getDbClassState, ...cleanData } = item;

		return {
			sysinfo: _kuzzle_info,
			...cleanData,
		};
	});
}

// ==Функции для внешних модулей==
// ====Создаем документы в указанном классе====
export const createLocalDocs = async (createLocalScheme: CreateLocalScheme) => {
	// Получаем коллекцию из базы
	const iCollection = R?.localData.db?.table(createLocalScheme.dbClass);

	// Если нет указанной коллекции, то сообщаем об ошибке
	if (!iCollection) {
		console.log(`Коллекция ${createLocalScheme.dbClass} не найдена в базе.`);
		R.libs.mantine.MantineError?.('Системная ошибка!', `Коллекция ${createLocalScheme.dbClass} не найдена в базе.`);
		return;
	}

	// Подготавливаем документы к записи
	const docsForCreate = renameSysInfo(createLocalScheme.items);

	// Если у документа нет sysinfo, то создаем
	for (const iDoc of docsForCreate) {
		// Создаем id документа
		if (!iDoc?.id) iDoc.id = R.libs.nanoid();

		// Создаем sysinfo, если его нет при - он будет при репликации
		if (!iDoc?.sysinfo) {
			iDoc.sysinfo = {
				author: R?.user?.content?.name,
				createdAt: R.libs.dayjs().valueOf(),
			};
		}
	}

	// Создаем документы в базе
	// // async function bulkUpdateWithResult(table, items) {
	// const bulkAddWithResult = async (table: any, docsForCreate: any[]) => {
	// 	// Выполняем транзакцию с 2мя операциями. Транзакция выполнится за один запрос к базе
	// 	return await R.localData.db.transaction('rw', table, async () => {
	// 		// Создаем документы, при этом получаем в ответе присвоенные id
	// 		const createdIds = await table.bulkAdd(docsForCreate, { allKeys: true });

	// 		// Получаем по id все документы
	// 		return await table.where('id').anyOf(createdIds).toArray();
	// 	});
	// };
	// const createResult = await iCollection.bulkAdd(docsForCreate, { allKeys: true });
	const createResult = await R.localData.db.transaction('rw', iCollection, async () => {
		// Создаем документы, при этом получаем в ответе присвоенные id
		const createdIds = await iCollection.bulkAdd(docsForCreate, { allKeys: true });

		// Получаем по id все документы
		const createdDocs = await iCollection.where('id').anyOf(createdIds).toArray();

		return {
			count: createdDocs.length,
			items: createdDocs,
		};
	});

	// await bulkAddWithResult(iCollection, docsForCreate);
	return { [createLocalScheme.dbClass]: createResult };
};

// ====Обновляем документы в указанном классе====
export const updateLocalDocs = async (updateLocalScheme: UpdateLocalScheme) => {
	// Получаем коллекцию из базы
	const iCollection = R?.localData.db?.table(updateLocalScheme.dbClass);

	// Если нет указанной коллекции, то сообщаем об ошибке
	if (!iCollection) {
		console.log(`Коллекция ${updateLocalScheme.dbClass} не найдена в базе.`);
		R.libs.mantine.MantineError?.('Системная ошибка!', `Коллекция ${updateLocalScheme.dbClass} не найдена в базе.`);
		return;
	}

	// Подготавливаем документы к записи
	const docsForUpdate = renameSysInfo(updateLocalScheme.items);

	// Обновляем sysinfo
	for (const iDoc of docsForUpdate) {
		iDoc.sysinfo = {
			// Создателя не изменяем
			author: iDoc?.sysinfo?.author || R?.user?.content?.name,
			createdAt: iDoc?.sysinfo?.createdAt || R.libs.dayjs().valueOf(),
			// Обновлятора меняем, только если был создатель
			updater: iDoc?.sysinfo?.author ? R?.user?.content?.name : undefined,
			updatedAt: iDoc?.sysinfo?.author ? R.libs.dayjs().valueOf() : undefined,
		};
	}

	// Получаем id документов, чтобы вернуть их после изменения
	const ids = docsForUpdate.map((iDoc) => iDoc.id)?.filter((fDocId) => !!fDocId);

	// Создаем map для хранения сортировки, чтобы выдавать результат в том же порядке, в котором подали
	const orderMap = new Map(ids.map((id, index) => [id, index]));

	// Выполняем транзакцию с операциями. Транзакция выполнится за один запрос к базе
	const updateResult = await R.localData.db.transaction('rw', iCollection, async () => {
		// Находим существующие документы
		const existingDocs = await iCollection.where('id').anyOf(ids).toArray();
		const existingIds = new Set(existingDocs.map((iDoc: any) => iDoc.id));

		// Разделяем на документы для обновления и создания
		const docsToUpdate = [];
		const docsToCreate = [];

		for (const iDoc of docsForUpdate) {
			// Если такой id есть в базе, то обновляем
			if (existingIds.has(iDoc.id)) {
				// has операции с Set работют быстрее, чем includes с массивами
				// Преобразуем документы в формат для обновления
				const { id, ...changes } = iDoc;
				docsToUpdate.push({ key: id, changes });
			}
			// А иначе создаем
			else {
				docsToCreate.push(iDoc);
			}
		}

		// Выполняем операции обновления
		if (docsToUpdate.length > 0) {
			await iCollection.bulkUpdate(docsToUpdate);
		}
		// И создания
		if (docsToCreate.length > 0) {
			await iCollection.bulkAdd(docsToCreate);
		}

		const updatedDocs = await iCollection.where('id').anyOf(ids).toArray();

		// Сортируем в исходном порядке
		const orderedResults = updatedDocs.sort((a: any, b: any) => {
			return (orderMap.get(a.id) ?? 0) - (orderMap.get(b.id) ?? 0);
		});

		return {
			count: orderedResults.length,
			items: orderedResults,
		};
	});

	return { [updateLocalScheme.dbClass]: updateResult };
};

// ====Удаляем документы из указанного класса====
export const deleteLocalDocs = async (deleteLocalScheme: DeleteLocalScheme) => {
	// Получаем коллекцию из базы
	const iCollection = R?.localData.db?.table(deleteLocalScheme.dbClass);

	// Если нет указанной коллекции, то сообщаем об ошибке
	if (!iCollection) {
		console.log(`Коллекция ${deleteLocalScheme.dbClass} не найдена в базе.`);
		R.libs.mantine.MantineError?.('Системная ошибка!', `Коллекция ${deleteLocalScheme.dbClass} не найдена в базе.`);
		return;
	}

	// Удаляем документы
	const deleteResult = await iCollection.bulkDelete(deleteLocalScheme.ids);

	return {
		[deleteLocalScheme.dbClass]: {
			count: deleteResult.length,
			ids: deleteResult,
		},
	};
};

// ====Получаем документы из указанного класса с сортировкой, фильтрами и пагинацией====
export const fetchLocalDocs = async (fetchLocalScheme: FetchLocalScheme) => {
	// Получаем коллекцию из базы
	const iCollection = R?.localData?.db?.[fetchLocalScheme.dbClass];

	// Если нет указанной коллекции, то сообщаем об ошибке
	if (!iCollection) {
		console.log(`Коллекция ${fetchLocalScheme.dbClass} не найдена в базе.`);
		R.libs.mantine.MantineError?.('Системная ошибка!', `Коллекция ${fetchLocalScheme.dbClass} не найдена в базе.`);
		return;
	}

	// // Проверяем наличие индексов (опционально) при сортировке
	// if (fetchLocalScheme.sorts?.length) {
	// 	const tableIndexes = iCollection.schema.indexes;
	// 	const hasNoIndexes = fetchLocalScheme.sorts.some((sortObj) => {
	// 		const field = Object.keys(sortObj)[0];
	// 		return !tableIndexes.includes(field);
	// 	});

	// 	if (hasNoIndexes) {
	// 		console.warn('⚠️ Некоторые поля сортировки не имеют индексов. Производительность может быть снижена.');
	// 	}
	// }

	// Находим параметры для сортировки или задаем стандартные
	const sortPath = fetchLocalScheme?.sorts?.[0] ? Object.keys(fetchLocalScheme.sorts[0])[0] : 'sysinfo.createdAt';
	const sortVector = fetchLocalScheme?.sorts?.[0] ? Object.values(fetchLocalScheme?.sorts?.[0])[0] : 'asc';

	// Порядок запросов важен, не менять на обум
	const fetchResult = await // Проверяем направление сортировки
	(sortVector === 'asc'
		? // Если от меньшего к большему - то это стандартное asc
		  iCollection.orderBy(sortPath)
		: // Если от большего к меньшему, то это desc, и надо перевернуть записи
		  iCollection.orderBy(sortPath).reverse()
	)
		// Фильтруем
		.filter(filterConvertation(fetchLocalScheme.filters))
		// Задаем пропуск документов from
		.offset(fetchLocalScheme?.from ?? 0)
		// Задаем ограничене количества документов size
		.limit(fetchLocalScheme?.size ?? 50)
		// Выдаем результаты в виде массива объектов
		.toArray();

	return fetchResult;
};

// ====Получаем количество документов из указанного класса с фильтрами====
export const getLocalDocsCount = async (fetchLocalScheme: FetchLocalScheme) => {
	// Получаем коллекцию из базы
	const iCollection = R?.localData?.db?.[fetchLocalScheme.dbClass];

	// Если нет указанной коллекции, то сообщаем об ошибке
	if (!iCollection) {
		console.log(`Коллекция ${fetchLocalScheme.dbClass} не найдена в базе.`);
		R.libs.mantine.MantineError?.('Системная ошибка!', `Коллекция ${fetchLocalScheme.dbClass} не найдена в базе.`);
		return;
	}

	// Порядок запросов важен, не менять на обум
	const docsCount = await iCollection.filter(filterConvertation(fetchLocalScheme.filters)).count();

	return docsCount;
};
